import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFolder,
  faFolderOpen,
  faChevronRight,
  faChevronDown,
  faPen,
  faTrash,
  faHouse,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { buildTree, isInside } from "@serie3/mymediaplanner/Modules/Documents/Domain/folderTree";

/** MIME-like key used for dragging a file card onto a folder. */
export const FILE_DRAG_TYPE = "application/x-mymediaplanner-file-id";

const ACTIVE_BG = "rgba(3,142,220,0.08)";
const DROP_BG = "rgba(81,210,140,0.18)";

/**
 * One row of the tree: selectable, a drop target for file cards, with rename
 * and delete on hover. A location's root row is the same component with
 * `node = null`; `root` then carries its label and kind.
 */
const FolderRow = ({
  sourceKey,
  root = null,
  node,
  depth,
  active,
  expanded,
  hasChildren,
  onSelect,
  onToggle,
  onDropFile,
  onRename,
  onDelete,
}) => {
  const [over, setOver] = useState(false);
  const [hover, setHover] = useState(false);
  const { t } = useTranslation();
  const isRoot = node === null;
  const path = isRoot ? null : node.path;

  const acceptsDrag = (e) => Array.from(e.dataTransfer?.types ?? []).includes(FILE_DRAG_TYPE);

  return (
    <div
      role="treeitem"
      aria-selected={active}
      aria-expanded={hasChildren ? expanded : undefined}
      data-folder-source={sourceKey}
      data-folder-path={path ?? ""}
      onClick={() => onSelect(sourceKey, path)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onDragOver={(e) => {
        if (!acceptsDrag(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (!over) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        if (!acceptsDrag(e)) return;
        e.preventDefault();
        setOver(false);
        // one id, or several comma-separated when a selection was dragged
        const ids = e.dataTransfer
          .getData(FILE_DRAG_TYPE)
          .split(",")
          .map((raw) => parseInt(raw, 10))
          .filter((id) => !Number.isNaN(id));
        if (ids.length > 0) onDropFile(ids, sourceKey, path);
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "7px 8px",
        paddingLeft: 8 + depth * 14,
        borderRadius: 8,
        cursor: "pointer",
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        color: active ? "#038edc" : "#374151",
        background: over ? DROP_BG : active ? ACTIVE_BG : hover ? "#f5f7fa" : "transparent",
        outline: over ? "1.5px dashed #51d28c" : "none",
        transition: "background 0.12s ease",
        userSelect: "none",
      }}
    >
      {hasChildren ? (
        <button
          type="button"
          aria-label={expanded ? "collapse" : "expand"}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(sourceKey, path);
          }}
          style={{ border: "none", background: "transparent", padding: 0, width: 14, color: "#9299a8", cursor: "pointer", fontSize: 10 }}
        >
          <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} />
        </button>
      ) : (
        <span style={{ width: 14, display: "inline-block" }} />
      )}
      <FontAwesomeIcon
        icon={isRoot ? (root?.isGroup ? faUsers : faHouse) : active || expanded ? faFolderOpen : faFolder}
        style={{ color: isRoot ? (root?.isGroup ? "#564ab1" : "#038edc") : "#f0a500", fontSize: 13, flexShrink: 0 }}
      />
      <span
        style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        title={isRoot ? root?.label ?? "" : path}
      >
        {isRoot ? root?.label ?? t("Backend.Files.Locations.Mine", { defaultValue: "My files" }) : node.name}
      </span>
      {!isRoot && !hover && node.file_count > 0 && (
        <span style={{ fontSize: 11, color: "#9299a8", fontWeight: 500 }}>{node.file_count}</span>
      )}
      {!isRoot && hover && (
        <span style={{ display: "flex", gap: 2 }} onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            title={t("Backend.Files.Folders.Rename", { defaultValue: "Rename folder" })}
            aria-label={t("Backend.Files.Folders.Rename", { defaultValue: "Rename folder" })}
            onClick={() => onRename(sourceKey, node)}
            style={{ border: "none", background: "transparent", padding: "0 4px", color: "#038edc", cursor: "pointer", fontSize: 11 }}
          >
            <FontAwesomeIcon icon={faPen} />
          </button>
          <button
            type="button"
            title={t("Backend.Files.Folders.Delete", { defaultValue: "Delete folder" })}
            aria-label={t("Backend.Files.Folders.Delete", { defaultValue: "Delete folder" })}
            onClick={() => onDelete(sourceKey, node)}
            style={{ border: "none", background: "transparent", padding: "0 4px", color: "#f34e4e", cursor: "pointer", fontSize: 11 }}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </span>
      )}
    </div>
  );
};

/** The folders of one location under its root row. */
const LocationTree = ({ root, current, collapsed, toggle, onSelect, onDropFile, onRename, onDelete }) => {
  const tree = useMemo(() => buildTree(root.folders), [root.folders]);
  const here = current?.source === root.key;
  const collapsedKey = (path) => `${root.key}:${path ?? ""}`;
  const isExpanded = (path) => !collapsed.has(collapsedKey(path)) || (here && isInside(current?.path, path));
  const rootExpanded = !collapsed.has(collapsedKey(null)) || (here && current?.path !== null);

  const renderNodes = (nodes, depth) =>
    nodes.map((node) => {
      const expanded = isExpanded(node.path);
      return (
        <div key={node.path} role="group">
          <FolderRow
            sourceKey={root.key}
            node={node}
            depth={depth}
            active={here && current?.path === node.path}
            expanded={expanded}
            hasChildren={node.children.length > 0}
            onSelect={onSelect}
            onToggle={toggle}
            onDropFile={onDropFile}
            onRename={onRename}
            onDelete={onDelete}
          />
          {expanded && node.children.length > 0 && renderNodes(node.children, depth + 1)}
        </div>
      );
    });

  return (
    <div role="group" data-location={root.key}>
      <FolderRow
        sourceKey={root.key}
        root={root}
        node={null}
        depth={0}
        active={here && current?.path === null}
        expanded={rootExpanded}
        hasChildren={tree.length > 0}
        onSelect={onSelect}
        onToggle={toggle}
        onDropFile={onDropFile}
        onRename={onRename}
        onDelete={onDelete}
      />
      {rootExpanded && renderNodes(tree, 1)}
    </div>
  );
};

/**
 * Sidebar folder tree over every location the file manager shows. `roots` is
 * `[{ key, label, isGroup, folders }]` with each location's flat API folder
 * list; `current` is `{ source, path }` (path null = that location's root).
 * Ancestors of the selection are always expanded, anything else remembers its
 * own collapsed state. Handlers receive the location key first.
 */
const FolderTree = ({ roots = [], current = null, onSelect, onDropFile, onRename, onDelete }) => {
  const [collapsed, setCollapsed] = useState(() => new Set());

  const toggle = (sourceKey, path) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      const key = `${sourceKey}:${path ?? ""}`;
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  return (
    <div role="tree" style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {roots.map((root, index) => (
        <div key={root.key} style={{ marginTop: index > 0 ? 6 : 0 }}>
          <LocationTree
            root={root}
            current={current}
            collapsed={collapsed}
            toggle={toggle}
            onSelect={onSelect}
            onDropFile={onDropFile}
            onRename={onRename}
            onDelete={onDelete}
          />
        </div>
      ))}
    </div>
  );
};

export default FolderTree;
